
import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d6hq4ctpk1ctp7d9b7p0';

const plans = [
  { value: '2hours', label: '2 Hours — ¥15,000' },
  { value: 'halfday', label: 'Half Day (4 Hours) — ¥30,000' },
  { value: 'fullday', label: 'Full Day (8 Hours) — ¥50,000' },
  { value: 'other', label: 'Other / Not sure yet' },
];

export default function BookingFormSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) {
      alert('Message must be 500 characters or less.');
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });

    setStatus('sending');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="booking-form" className="py-24 bg-gradient-to-b from-black to-gray-900">
      <div className="max-w-3xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-block bg-amber-500/10 border border-amber-500/30 rounded-full px-6 py-2 mb-6">
            <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Inquiry & Booking</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Book Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-yellow-600">Assistant</span>
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed">
            Fill in the form below and we'll get back to you within 24 hours.<br />
            You can also reach us directly via LINE.
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-2xl p-8 md:p-10">
          {status === 'success' ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-amber-500/20 mx-auto mb-6">
                <i className="ri-check-line text-amber-400 text-4xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Thank you!</h3>
              <p className="text-gray-400 leading-relaxed">
                Your inquiry has been received.<br />
                We'll contact you within 24 hours.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-8 bg-amber-500/20 border border-amber-500/50 text-amber-400 px-8 py-3 rounded-full font-semibold hover:bg-amber-500/30 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Send Another Inquiry
              </button>
            </div>
          ) : (
            <form data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  placeholder="e.g. John Smith"
                  className="w-full bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address <span className="text-amber-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="e.g. john@example.com"
                  className="w-full bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              {/* Plan */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preferred Plan <span className="text-amber-400">*</span>
                </label>
                <select
                  name="plan"
                  required
                  defaultValue=""
                  className="w-full bg-gray-900/60 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select a plan...</option>
                  {plans.map((p) => (
                    <option key={p.value} value={p.label}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Preferred Date <span className="text-amber-400">*</span>
                </label>
                <input
                  type="date"
                  name="preferred_date"
                  required
                  className="w-full bg-gray-900/60 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
                />
              </div>

              {/* Pickup Location */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Pickup Location
                </label>
                <input
                  type="text"
                  name="pickup_location"
                  placeholder="e.g. Narita Airport, Shinjuku Station..."
                  className="w-full bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              {/* Number of Passengers */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Number of Passengers
                </label>
                <select
                  name="passengers"
                  defaultValue=""
                  className="w-full bg-gray-900/60 border border-gray-600 text-white rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors cursor-pointer appearance-none"
                >
                  <option value="" disabled>Select...</option>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={`${n} person${n > 1 ? 's' : ''}`}>{n} person{n > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Message / Special Requests
                  <span className="text-gray-500 font-normal ml-2">({charCount}/500)</span>
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="Any special requests, destinations, or questions..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500 transition-colors resize-none"
                />
              </div>

              {/* Error */}
              {status === 'error' && (
                <p className="text-red-400 text-sm text-center">
                  Something went wrong. Please try again or contact us via LINE.
                </p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={status === 'sending'}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-bold py-4 rounded-xl text-lg hover:from-amber-400 hover:to-yellow-500 transition-all duration-300 shadow-lg hover:shadow-amber-500/40 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {status === 'sending' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-xl"></i>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-send-plane-fill text-xl"></i>
                    Send Inquiry
                  </span>
                )}
              </button>

              {/* LINE Alternative */}
              <div className="text-center pt-2">
                <p className="text-gray-500 text-sm mb-3">Or book directly via LINE for faster response</p>
                <a
                  href="https://line.me/ti/p/~yokota0809"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#06C755] text-white px-8 py-3 rounded-full font-semibold text-sm hover:bg-[#05b34c] transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-line-fill text-xl"></i>
                  Book via LINE
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
